import { useState } from 'react'

// Spec 1.1/1.2. `#EEF3EC` has no brand.* token, written as literals. Chevron path is the
// drawn expand button's own path (spec 2.1); apex down (collapsed) is this component's own mirror --
// no down-chevron variant exists anywhere in the export (spec 5.1).
function ExpandChevron({ up }) {
  const d = up ? 'M21 19L16 14L11 19' : 'M11 14L16 19L21 14'
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path d={d} stroke="#4F5F4A" />
    </svg>
  )
}

// Section collapse has no drawn frame at all -- see README.md ("section collapse has no drawn
// state"). firstRowGap: 56 vs 64 header-to-first-row drift between the two sections (spec 1.8) --
// 8 is Loan Costs' own gap; pass 16 for Acquisition Costs. No number badge on any card (SOW 4 canvas:
// "Remove numbering of sections"): the title starts at the 24 padding. `plain`: no header total and no
// collapse button, always expanded -- neither right-column card (Flip Summary, Refinance) draws them.
// Classes, not an inline style: an inline outline colour would outrank the hover/focus-within ones.
export const CARD_RING_CLS =
  'outline outline-1 -outline-offset-1 outline-brand-fieldBdr hover:outline-[#9FB497] focus-within:outline-[#9FB497]'

export default function SectionCard({ title, total, firstRowGap = 8, plain = false, children }) {
  const [expandedState, setExpanded] = useState(true)
  const expanded = plain || expandedState

  return (
    // An inset `outline`, not a `border`: a CSS border on a border-box element consumes 2px of the
    // content it's drawn around, which is exactly the qa-verifier-measured 437-vs-439 content-width
    // defect -- Figma's own centred stroke (spec 0.2) never actually ate into the card's content.
    // `-outline-offset-1` pulls the ring fully inside the box's own edge. Letter 99 (5.5): the ring
    // goes #9FB497 on hover and while focus is anywhere inside -- see README.md ("card focus ring").
    <div className={`w-full bg-white rounded-[8px] pt-[16px] px-[24px] pb-[24px] ${CARD_RING_CLS}`}>
      <div className="flex items-center h-[32px]">
        {/* A heading, like Flip Summary's inside the Core Inputs card; preflight zeroes h3 margins, so the look is the span's. */}
        <h3 className="text-[17px] font-extrabold text-brand-dark">{title}</h3>
        {plain ? null : (
          <span className="flex-1 text-right text-[17px] font-extrabold text-brand-secondary pr-[8px]">
            {total ?? <span className="text-[17px] font-normal text-brand-placeholder">{'—'}</span>}
          </span>
        )}
        {plain ? null : (
          <button
            type="button"
            tabIndex={-1}
            aria-expanded={expanded}
            aria-label={expanded ? `Collapse ${title}` : `Expand ${title}`}
            onClick={() => setExpanded((v) => !v)}
            className="shrink-0 flex items-center justify-center w-[32px] h-[32px] rounded-[16px] bg-[#EEF3EC] hover:bg-[#DDE9D8] outline-none focus-visible:ring-2 focus-visible:ring-brand-green"
          >
            <ExpandChevron up={expanded} />
          </button>
        )}
      </div>
      {expanded ? <div style={{ marginTop: firstRowGap }}>{children}</div> : null}
    </div>
  )
}
